import React from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';

export default function Pouch3D({ mouseX, mouseY }) {
  // Parallax configuration for smooth, premium feel
  const springConfig = { damping: 25, stiffness: 100, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Map mouse position to rotation and translation
  const rotateX = useTransform(smoothY, [-1, 1], [10, -10]);
  const rotateY = useTransform(smoothX, [-1, 1], [-15, 15]);
  const translateX = useTransform(smoothX, [-1, 1], [-25, 25]);
  const translateY = useTransform(smoothY, [-1, 1], [-15, 15]);

  return (
    <motion.div
      className="relative z-10 perspective-[1500px] mb-8 sm:mb-12 mt-4 md:mt-0"
      initial={{ opacity: 0, scale: 0.85, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} // Custom Apple-like easing
      style={{
        x: translateX,
        y: translateY,
      }}
    >
      <motion.div
        style={{ rotateX, rotateY }}
        className="relative transform-style-3d"
      >
        {/* Continuous slow breathing/floating animation */}
        <motion.div
          animate={{ 
            rotateY: [-4, 4, -4],
            y: [-8, 8, -8]
          }}
          transition={{ 
            rotateY: { duration: 14, repeat: Infinity, ease: "easeInOut" },
            y: { duration: 7, repeat: Infinity, ease: "easeInOut" }
          }}
          className="relative w-48 h-[17rem] sm:w-64 sm:h-[22rem]"
        >
          {/* Main Pouch Body (CSS Representation of a premium matte tin/pouch) */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1c4d44] via-[#0f332d] to-[#071714] rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_40px_80px_rgba(0,0,0,0.7)] border border-brand-cream/10 overflow-hidden flex flex-col items-center justify-between py-5 sm:py-7">
            
            {/* Subtle surface texture */}
            <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none" 
                 style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />

            {/* Top Seal / Lid area */}
            <div className="w-[85%] h-6 sm:h-8 bg-gradient-to-r from-[#0a201d] via-[#1a4a42] to-[#0a201d] rounded-full shadow-[inset_0_2px_4px_rgba(255,255,255,0.1)] border border-black/40 flex items-center justify-center relative z-10">
                <div className="w-[70%] h-[1px] bg-black/50 rounded-full" />
            </div>

            {/* Main Label */}
            <div className="w-[85%] flex-1 my-4 sm:my-5 bg-brand-cream rounded-xl sm:rounded-2xl shadow-[inset_0_0_30px_rgba(0,0,0,0.05),0_10px_20px_rgba(0,0,0,0.2)] p-4 flex flex-col items-center justify-center relative overflow-hidden z-10">
               
               {/* Label subtle Japanese paper texture */}
               <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:8px_8px]" />

               <h3 className="font-serif text-3xl sm:text-5xl font-black text-brand-green tracking-tighter relative z-10 leading-none">MEE</h3>
               <p className="text-brand-red text-[10px] sm:text-xs tracking-[0.35em] mt-2 font-bold relative z-10 ml-1">MATCHA</p>

               {/* Stamp / Emblem */}
               <div className="mt-6 sm:mt-8 w-12 h-12 sm:w-14 sm:h-14 rounded-full border-[1.5px] border-brand-gold flex items-center justify-center relative z-10">
                 <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-brand-green flex items-center justify-center shadow-inner">
                    <span className="text-brand-cream text-xs sm:text-sm font-serif italic">A</span>
                 </div>
               </div>
            </div>

            {/* Bottom Base */}
            <div className="w-[90%] h-3 sm:h-4 bg-black/30 rounded-[100%] blur-[3px] absolute bottom-2" />

            {/* 3D Volumetric Lighting Overlay for the pouch */}
            <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-transparent to-white/15 pointer-events-none rounded-[2rem] sm:rounded-[2.5rem] z-20" />
            
            {/* Edge highlight */}
            <div className="absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-white/10 to-transparent pointer-events-none z-20" />
          </div>

          {/* Cast Shadow below the floating pouch */}
          <div className="absolute -bottom-12 sm:-bottom-16 left-1/2 -translate-x-1/2 w-[70%] h-8 bg-black/60 blur-[20px] rounded-[100%]" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}